import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Database } from 'lucide-react';
import { AppButton } from '../ui/AppButton';
import { AppModal } from '../ui/AppModal';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';
import { useWallet } from '../../hooks/useWallet';
import { dataAssetsAPI } from '../../services/api';
import { registerDataAsset } from '../../services/blockchainClient';
import { DATA_ASSET_TYPES, DATA_ASSET_TYPE_LABELS } from '../../config/contracts';

const emptyForm = {
  dataType: String(DATA_ASSET_TYPES.STATIC_BUILDING),
  contentHash: '',
  metadataURI: '',
  buildingId: '',
  coverageStart: '',
  coverageEnd: '',
  priceInLBT: '',
};

const refreshNotifications = () => {
  window.dispatchEvent(new Event('notifications:refresh'));
};

const getShortTxErrorMessage = (error, fallbackMessage) => {
  const message = String(
    error?.response?.data?.error ||
    error?.shortMessage ||
    error?.reason ||
    error?.message ||
    fallbackMessage
  );
  const lower = message.toLowerCase();
  if (lower.includes('insufficient funds')) return 'Insufficient funds';
  if (lower.includes('out of gas') || lower.includes('intrinsic gas')) return 'Gas too low';
  if (lower.includes('timeout') || lower.includes('timed out')) return 'Confirmation timed out';
  if (lower.includes('revert') || lower.includes('call exception')) return 'Reverted by contract';
  return message;
};

/**
 * Data asset registry panel: browse registered assets and register new ones on-chain.
 */
export const DataAssetPanel = () => {
  const { signer, isConnected, isNetworkMismatch } = useWallet();

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dataAssetsAPI.listAssets();
      setAssets(data || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load data assets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleRegister = async () => {
    if (!isConnected) { toast.error('Connect wallet first'); return; }
    if (isNetworkMismatch) { toast.error('Switch to expected network first'); return; }
    if (!form.contentHash || !form.buildingId) {
      toast.error('Content hash and Building ID are required');
      return;
    }

    setRegistering(true);
    try {
      const coverageStartTs = form.coverageStart
        ? Math.floor(new Date(form.coverageStart).getTime() / 1000)
        : 0;
      const coverageEndTs = form.coverageEnd
        ? Math.floor(new Date(form.coverageEnd).getTime() / 1000)
        : 0;

      toast.info('Transaction submitted: Please confirm data asset registration.');
      const tx = await registerDataAsset({
        signer,
        dataType: Number(form.dataType),
        contentHash: form.contentHash,
        metadataURI: form.metadataURI || '',
        buildingId: form.buildingId,
        coverageStart: coverageStartTs,
        coverageEnd: coverageEndTs,
        priceInLBT: form.priceInLBT || '0',
      });
      toast.success('Transaction confirmed: Data asset registration was confirmed on-chain.');

      const asset = await dataAssetsAPI.createAsset({
        on_chain_id: tx.assetId,
        data_type: Number(form.dataType),
        content_hash: form.contentHash,
        metadata_uri: form.metadataURI || null,
        building_id: form.buildingId,
        coverage_start: form.coverageStart || null,
        coverage_end: form.coverageEnd || null,
        price_in_lbt: form.priceInLBT || '0',
        transaction_hash: tx.txHash,
      });

      const assetName = asset.name || asset.metadata_uri || asset.content_hash || asset.id;
      toast.success(`New data asset registered: Data asset "${assetName}" has been registered.`);
      refreshNotifications();
      setShowModal(false);
      setForm(emptyForm);
      await fetchAssets();
    } catch (error) {
      toast.error(`Transaction failed: ${getShortTxErrorMessage(error, 'Failed to register data asset')}`);
    } finally {
      setRegistering(false);
    }
  };

  return (
    <section className="panel-card" aria-labelledby="assets-title">
      <div className="panel-header">
        <h2 id="assets-title">Data Asset Registry</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={fetchAssets} className="px-3 py-1.5 rounded-card-sm text-[12px] font-semibold text-lb-body hover:text-lb-ink hover:bg-black/[0.04] transition-colors focus-ring outline-none">
            Refresh
          </button>
          <AppButton type="button" variant="primary" onClick={() => setShowModal(true)}>
            Register Asset
          </AppButton>
        </div>
      </div>

      {loading ? (
        <SkeletonLoader rows={4} />
      ) : assets.length === 0 ? (
        <EmptyState
          icon={Database}
          title="No data assets registered"
          message="Register a data asset on-chain using the button above."
        />
      ) : (
        <div className="offer-grid">
          {assets.map((asset) => (
            <article key={asset.id} className="offer-card">
              <h3>{DATA_ASSET_TYPE_LABELS[asset.data_type] ?? `Type ${asset.data_type}`}</h3>
              <p><strong>Building:</strong> {asset.building_id}</p>
              <p><strong>Hash:</strong> {String(asset.content_hash || '').slice(0, 20)}…</p>
              <p><strong>On-chain ID:</strong> {asset.on_chain_id ?? '—'}</p>
              <p><strong>Price:</strong> {asset.price_in_lbt} LBT</p>
              {asset.coverage_start && (
                <p>
                  <strong>Coverage:</strong>{' '}
                  {new Date(asset.coverage_start).toLocaleDateString()}{' '}
                  – {asset.coverage_end ? new Date(asset.coverage_end).toLocaleDateString() : '?'}
                </p>
              )}
              {asset.eu_bso_eligible && (
                <span className="status-badge active" style={{ fontSize: '0.7rem' }}>EU BSO Eligible</span>
              )}
              {!asset.active && (
                <span className="status-badge inactive" style={{ fontSize: '0.7rem' }}>Inactive</span>
              )}
              {asset.transaction_hash && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted, #888)' }}>
                  Tx: {asset.transaction_hash.slice(0, 18)}…
                </p>
              )}
            </article>
          ))}
        </div>
      )}

      <AppModal
        open={showModal}
        title="Register Data Asset"
        onClose={() => setShowModal(false)}
        footer={
          <>
            <AppButton variant="ghost" onClick={() => setShowModal(false)}>Cancel</AppButton>
            <AppButton variant="primary" onClick={handleRegister} disabled={registering}>
              {registering ? 'Registering…' : 'Register On-Chain'}
            </AppButton>
          </>
        }
      >
        <div className="form-grid">
          <label>
            Data Type
            <select
              value={form.dataType}
              onChange={(e) => setForm((f) => ({ ...f, dataType: e.target.value }))}
            >
              {Object.entries(DATA_ASSET_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <label>
            Content Hash
            <input
              value={form.contentHash}
              onChange={(e) => setForm((f) => ({ ...f, contentHash: e.target.value }))}
              placeholder="0x…"
            />
          </label>
          <label>
            Metadata URI (optional)
            <input
              value={form.metadataURI}
              onChange={(e) => setForm((f) => ({ ...f, metadataURI: e.target.value }))}
              placeholder="ipfs://…"
            />
          </label>
          <label>
            Building ID
            <input
              value={form.buildingId}
              onChange={(e) => setForm((f) => ({ ...f, buildingId: e.target.value }))}
              placeholder="Building reference"
            />
          </label>
          <label>
            Coverage Start
            <input
              type="date"
              value={form.coverageStart}
              onChange={(e) => setForm((f) => ({ ...f, coverageStart: e.target.value }))}
            />
          </label>
          <label>
            Coverage End
            <input
              type="date"
              value={form.coverageEnd}
              onChange={(e) => setForm((f) => ({ ...f, coverageEnd: e.target.value }))}
            />
          </label>
          <label>
            Price (LBT)
            <input
              type="number"
              min="0"
              value={form.priceInLBT}
              onChange={(e) => setForm((f) => ({ ...f, priceInLBT: e.target.value }))}
              placeholder="0"
            />
          </label>
        </div>
      </AppModal>
    </section>
  );
};
